import React, { useState, useEffect } from 'react';
import { Trash2, Pencil, Link2, Loader2, CheckCircle } from 'lucide-react';
import { api } from '../api';
import { EditCourseModal } from '../components/modals/EditCourseModal';

const ManageCourses = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCourse, setEditingCourse] = useState(null);
  const [linking, setLinking] = useState(false);
  const [linkResult, setLinkResult] = useState(null);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const data = await api.request('/courses');
      // API returns array directly, not {courses: [...]}
      setCourses(Array.isArray(data) ? data : data.courses || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const deleteCourse = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;
    try {
      await api.request(`/courses/${courseId}`, { method: 'DELETE' });
      fetchCourses();
    } catch (err) {
      alert(err.message);
    }
  };

  const runAutoLink = async () => {
    if (!window.confirm('This will scan all unlinked courses and auto-link any with a matching catalog title. Continue?')) return;
    setLinking(true);
    setLinkResult(null);
    try {
      const data = await api.request('/courses/bulk-link-catalog', { method: 'POST' });
      setLinkResult(data);
      fetchCourses();
    } catch (err) {
      setLinkResult({ error: err.message || 'Auto-link failed.' });
    } finally {
      setLinking(false);
    }
  };

  return (
    <div className="space-y-4">
      {editingCourse && (
        <EditCourseModal
          course={editingCourse}
          onClose={() => setEditingCourse(null)}
          onSuccess={() => { setEditingCourse(null); fetchCourses(); }}
        />
      )}
      <div className="bg-levelup-dark rounded-xl p-4 text-white flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold">Manage Courses</h1>
          <p className="text-gray-300 mt-1">View and manage all course content</p>
        </div>
        <button
          onClick={runAutoLink}
          disabled={linking}
          className="flex items-center gap-2 px-4 py-2 bg-levelup-accent text-levelup-darker rounded-lg font-semibold text-sm hover:bg-levelup-accent-hover disabled:opacity-50 transition-all"
          data-testid="auto-link-catalog-btn"
        >
          {linking ? <Loader2 className="animate-spin" size={16} /> : <Link2 size={16} />}
          {linking ? 'Linking…' : 'Auto-Link Matching Courses'}
        </button>
      </div>
      {linkResult && (
        <div
          className={`rounded-xl p-4 border ${linkResult.error ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-800'}`}
          data-testid="auto-link-result"
        >
          {linkResult.error ? (
            <p>{linkResult.error}</p>
          ) : (
            <div className="flex items-center gap-2">
              <CheckCircle size={16} />
              <p>Checked {linkResult.checked_count} unlinked course(s), linked {linkResult.linked_count} to matching catalog titles.</p>
            </div>
          )}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-levelup-accent border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : courses.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No courses found. Start by uploading content.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Title</th>
                  <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Solution Area</th>
                  <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Search Tags</th>
                  <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Catalog</th>
                  <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Files</th>
                  <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {courses.map(course => (
                  <tr key={course.id} data-testid={`manage-course-${course.id}`}>
                    <td className="px-3 py-2">
                      <p className="font-medium text-levelup-dark">{course.title}</p>
                      <p className="text-sm text-gray-500">{course.level} - {course.duration}</p>
                    </td>
                    <td className="px-3 py-2 text-gray-600">{course.solution_area}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {(course.search_tags || []).slice(0, 2).map(tag => (
                          <span key={tag} className="px-2 py-0.5 bg-levelup-accent/20 text-levelup-darker text-xs rounded-full font-medium">#{tag}</span>
                        ))}
                        {(course.search_tags || []).length > 2 && (
                          <span className="text-xs text-gray-400">+{(course.search_tags || []).length - 2} more</span>
                        )}
                        {(course.search_tags || []).length === 0 && (
                          <span className="text-xs text-gray-300">—</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      {course.catalog_course_id ? (
                        <span className="flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium w-fit" data-testid={`catalog-linked-${course.id}`}>
                          <Link2 size={11} /> Linked
                        </span>
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className="px-2 py-1 bg-levelup-dark/10 text-levelup-dark rounded text-sm">
                        {course.files?.length || 0} files
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setEditingCourse(course)}
                          className="p-2 text-levelup-accent hover:bg-levelup-accent/10 rounded"
                          data-testid={`edit-course-${course.id}`}
                        >
                          <Pencil size={18} />
                        </button>
                        <button
                          onClick={() => deleteCourse(course.id)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded"
                          data-testid={`delete-course-${course.id}`}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// About Skilling In a Box Page Component

export default ManageCourses;
